// Processes a dropped folder in the studio's browser: model, camera points, renders, levels and
// headings. Nothing is uploaded here; the check step saves the result.
import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import {
  TourEngine, loadModelFile, modelFormatOf, parseCameraFile, cameraToWorld, matchCamera, detectUnits,
  imageEdges, detectHeadings, panoTextureFrom, loadPanoTexture, levelName, assignLevels as assignToLevels, type CameraFile, type CameraPoint, type EnginePano,
  readExr, fitDepthByViews, applyDepthFit, DepthProxyBuilder, headingFromCamera, type DepthFit,
} from '@archviewvr/engine';
import { depthName, depthPng, matchDepth, cameraPoint, nearestTo, SCENE_UNITS } from './depthfiles';

export type StepState = 'wait' | 'busy' | 'ok' | 'warn' | 'bad';
export type StepId = 'model' | 'cams' | 'renders' | 'levels' | 'headings';
export interface Step { id: StepId; title: string; detail: string; state: StepState; progress?: number }

export interface WorkPano {
  id: string;
  name: string;
  /** null for a render already saved in the project and not in this drop (kept as it is). */
  file: File | null;
  /** Already saved and not in this drop: its files and row stay. */
  kept?: boolean;
  /** Same name as a saved render: this one overwrites it and keeps its id (rooms, start view, stats). */
  replaces?: boolean;
  thumbUrl: string;
  thumb: Blob | null;
  base: Blob | null;
  mobile: Blob | null;
  full: Blob | null;
  status: 'busy' | 'ok' | 'warn' | 'bad';
  note: string;
  level: number | null;
  ep: EnginePano | null;
  /** Name of the camera it matched in the points file. */
  cam?: string;
  /** Another render on the same camera (another finish): saved as an alternate panorama of this main render (id). */
  altOf?: string;
  /** Its scheme name, worked out from the file names (e.g. Dark), for renders that share a camera. */
  scheme?: string;
  /** Its depth map, ready to upload (PNG), from a CZDepth EXR in the drop. */
  depthPng?: Blob | null;
  /** Heading values offered on the check screen, degrees. */
  headingOwn?: number;
  headingShared?: number;
}

export type WorkEvent = { kind: 'step'; id: StepId } | { kind: 'pano'; pano: WorkPano; added: boolean } | { kind: 'done' } | { kind: 'storage' };

export interface Work {
  projectId: string;
  steps: Step[];
  panos: WorkPano[];
  modelBlob: Blob | null;
  modelName: string;
  camFile: CameraFile | null;
  levels: number[];
  /** The model as read from its file (the dropped file, or the saved GLB, which is in metres). */
  modelRoot: THREE.Object3D | null;
  /** How the model file's units were read, e.g. "centimetres". */
  modelUnits: string;
  /** Scale from the original model file's units to the units of `modelRoot`: 1 for a dropped file,
   * the original units for a saved GLB (already in metres). So file units → metres = modelScale × fileUnit. */
  fileUnit: number;
  /** False for a saved model from before the original units were recorded. */
  fileUnitKnown: boolean;
  /** The model's scale changed on the check screen, so it's written again on save. */
  rescaled: boolean;
  /** When the model's scale changes, the cameras move with it (their points were in model units). */
  scaleCams: boolean;
  /** Opened from the saved project to adjust it, with nothing new dropped. */
  fromSaved: boolean;
  /** What the project already has, when this drop updates it. */
  base: Base | null;
  /** When renders share cameras: the scheme kept as the main renders (e.g. Light). */
  mainScheme?: string;
  /** No model file: the proxy was made from the depth maps (metres; its scale can't change). */
  depthBuilt?: boolean;
  engine: TourEngine;
  canvas: HTMLCanvasElement;
  running: boolean;
  done: boolean;
  failed: boolean;
  listeners: Set<(e: WorkEvent) => void>;
}

/** A saved project's model and renders, so a drop can add to or overwrite parts of it. */
export interface Base {
  modelUrl: string;
  /** Units of the model file the saved GLB was made from (its scale to metres), when known. */
  modelUnit?: number;
  /** The saved model was made from depth maps (no model file): dropping depth maps again makes it again. */
  depthModel?: boolean;
  /** Saved floor heights, reused while the model stays the same. */
  levels: number[];
  panos: Array<{ id: string; name: string; x: number; y: number; z: number; yaw: number; thumbUrl: string; imageUrl: string; room?: string | null }>;
}

const works = new Map<string, Work>();
export const getWork = (projectId: string) => works.get(projectId) ?? null;
export function dropWork(projectId: string): void {
  const w = works.get(projectId);
  if (!w) return;
  w.engine.dispose();
  w.panos.forEach((p) => { if (p.thumbUrl.startsWith('blob:')) URL.revokeObjectURL(p.thumbUrl); });
  works.delete(projectId);
}

const isTouch = () => matchMedia('(pointer: coarse)').matches;
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r(null)));
const natural = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const baseName = (n: string) => n.replace(/^.*[\\/]/, '').replace(/\.[^.]+$/, '');
const isImage = (f: File) => /^image\/(jpeg|png|webp)$/.test(f.type) || /\.(jpe?g|png|webp)$/i.test(f.name);
const isCams = (f: File) => /\.(txt|csv|json)$/i.test(f.name);
const isExr = (f: File) => /\.exr$/i.test(f.name);

/** A CZDepth EXR from the drop: its camera (from 3ds Max) and a small copy for working out the depth range. */
interface DepthIn { file: File; name: string; cam: number[] | null; small: Float32Array; sw: number; sh: number }

/** Every nth pixel of a depth file, for fitting (the full file is read again when it's saved). */
function shrink(raw: Float32Array, w: number, h: number, maxW: number): { data: Float32Array; w: number; h: number } {
  const s = Math.max(1, Math.round(w / maxW)), ow = Math.floor(w / s), oh = Math.floor(h / s), out = new Float32Array(ow * oh);
  for (let y = 0; y < oh; y++) for (let x = 0; x < ow; x++) out[y * ow + x] = raw[y * s * w + x * s];
  return { data: out, w: ow, h: oh };
}

export function newWork(projectId: string): Work {
  dropWork(projectId);
  const canvas = document.createElement('canvas');
  const engine = new TourEngine(canvas, { spin: true, markers: true, settle: false, sharpSize: 0 });
  const w: Work = {
    projectId,
    steps: [
      { id: 'model', title: 'Model', detail: 'Waiting', state: 'wait' },
      { id: 'cams', title: 'Camera points', detail: 'Waiting', state: 'wait' },
      { id: 'renders', title: 'Renders', detail: 'Waiting', state: 'wait' },
      { id: 'levels', title: 'Levels', detail: 'Waiting', state: 'wait' },
      { id: 'headings', title: 'Headings', detail: 'Waiting', state: 'wait' },
    ],
    panos: [], modelBlob: null, modelName: '', camFile: null, levels: [1.6], base: null,
    modelRoot: null, modelUnits: 'metres', fileUnit: 1, fileUnitKnown: true, rescaled: false, scaleCams: true, fromSaved: false,
    engine, canvas, running: false, done: false, failed: false, listeners: new Set(),
  };
  works.set(projectId, w);
  return w;
}

function emit(w: Work, e: WorkEvent): void { w.listeners.forEach((fn) => fn(e)); }
function setStep(w: Work, id: StepId, patch: Partial<Step>): void {
  Object.assign(w.steps.find((s) => s.id === id)!, patch);
  emit(w, { kind: 'step', id });
}

/** Bytes this project will store once saved. */
export function storageOf(w: Work, panos = w.panos): { renders: number; model: number; total: number } {
  const renders = panos.reduce((s, p) => s + (p.base?.size ?? 0) + (p.mobile?.size ?? 0) + (p.full?.size ?? 0) + (p.thumb?.size ?? 0) + (p.depthPng?.size ?? 0), 0);
  const model = w.modelBlob?.size ?? 0;
  return { renders, model, total: renders + model };
}

function canvasBlob(src: CanvasImageSource, sw: number, sh: number, w: number, h: number, sx = 0, sy = 0, quality = 0.85): Promise<Blob> {
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
  return new Promise((res, rej) => cv.toBlob((b) => (b ? res(b) : rej(new Error('Could not encode image'))), 'image/jpeg', quality));
}

/** The sizes saved for one render: thumbnail, 2K for blending, 1K for phones and the full-size image. */
export async function renderSizes(f: File): Promise<{ thumb: Blob; base: Blob; mobile: Blob; full: Blob; width: number; height: number }> {
  const bmp = await createImageBitmap(f);
  const W = bmp.width, H = bmp.height;
  const thumb = await canvasBlob(bmp, W * 0.32, H * 0.4, 480, 300, W * 0.34, H * 0.3, 0.8);
  const base = await canvasBlob(bmp, W, H, Math.min(2048, W), Math.min(1024, H));
  const mobile = await canvasBlob(bmp, W, H, Math.min(1024, W), Math.min(512, H), 0, 0, 0.82);
  const full = /jpe?g$/i.test(f.type || f.name) && W <= 8192 ? f : await canvasBlob(bmp, W, H, Math.min(8192, W), Math.min(4096, H), 0, 0, 0.9);
  bmp.close();
  return { thumb, base, mobile, full, width: W, height: H };
}

function exportGlb(obj: THREE.Object3D): Promise<Blob> {
  return new Promise((resolve, reject) => {
    new GLTFExporter().parse(obj, (result: ArrayBuffer | object) => {
      resolve(new Blob([result instanceof ArrayBuffer ? result : JSON.stringify(result)], { type: 'model/gltf-binary' }));
    }, (err: unknown) => reject(err instanceof Error ? err : new Error('Could not convert the model')), { binary: true });
  });
}

/** Runs the whole pipeline on a set of dropped files. */
export async function processFiles(w: Work, files: File[]): Promise<void> {
  w.running = true;
  const engine = w.engine;
  const model = files.find((f) => modelFormatOf(f.name));
  const cams = files.find(isCams);
  const exrs = files.filter(isExr).sort((a, b) => natural(a.name, b.name));
  // no model file, but depth maps: the proxy is made from them once the renders are placed
  // (a tour whose model was made that way makes it again, with the renders it has)
  const depthOnly = !model && exrs.length > 0 && (!w.base || !!w.base.depthModel);
  // two renders with the same name (a .png beside a .jpg, or a copy in another folder): keep the newest
  const newest = new Map<string, File>();
  for (const f of files.filter(isImage)) {
    const k = baseName(f.name).toLowerCase();
    const o = newest.get(k);
    if (!o || f.lastModified > o.lastModified) newest.set(k, f);
  }
  const images = [...newest.values()].sort((a, b) => natural(a.name, b.name));
  const dupes = files.filter(isImage).length - images.length;

  const base = w.base;
  // 1 · model (an update without one keeps the saved model)
  if (!model && base && !depthOnly) {
    setStep(w, 'model', { state: 'busy', detail: 'Loading the saved model' });
    try {
      const res = await fetch(base.modelUrl, { cache: 'no-store' });
      if (!res.ok) throw new Error('not found');
      const root = await loadModelFile(new File([await res.blob()], 'model.glb'));
      engine.setModel(root, 1, false);
      w.modelRoot = root; w.modelUnits = 'metres';
      w.fileUnit = base.modelUnit && base.modelUnit > 0 ? base.modelUnit : 1;
      w.fileUnitKnown = !!(base.modelUnit && base.modelUnit > 0);
      const sz = engine.bbox.getSize(new THREE.Vector3());
      w.modelName = 'model.glb';
      setStep(w, 'model', { state: 'ok', detail: base.depthModel
        ? `Keeping the saved model, made from depth maps · ${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m. Drop the EXRs too to make it again, or a model file to replace it.`
        : `Keeping the saved model · ${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m. Drop a new one to replace it.` });
    } catch {
      setStep(w, 'model', { state: 'bad', detail: 'Couldn’t load the saved model. Add the model to the drop.' });
      return finish(w, true);
    }
  }
  if (depthOnly) setStep(w, 'model', { state: 'busy', detail: base ? `Making the model again from the ${exrs.length} depth maps once the renders are placed` : `No model file: one will be made from the ${exrs.length} depth maps once the renders are placed` });
  else if (!model && !base) { setStep(w, 'model', { state: 'bad', detail: 'No model found. Add a .glb, .gltf or .obj file, or the CZDepth EXRs of your renders to make one from them.' }); return finish(w, true); }
  let scale = 1;
  if (model) try {
    setStep(w, 'model', { state: 'busy', detail: `Reading ${model.name}` });
    await nextFrame();
    const root = await loadModelFile(model);
    const raw = new THREE.Box3().setFromObject(root).getSize(new THREE.Vector3());
    // the ArchviewVR Toolkit writes the model's units into the points file; otherwise they're worked out from its size
    let recorded: number | undefined;
    if (cams && /\.json$/i.test(cams.name)) { try { recorded = parseCameraFile(await cams.text()).modelScale; } catch { /* read again below */ } }
    const named: Record<string, string> = { 1: 'metres', 0.01: 'centimetres', 0.001: 'millimetres', 1000: 'kilometres', 0.0254: 'inches', 0.3048: 'feet' };
    const units = recorded ? { scale: recorded, name: `${named[String(recorded)] ?? `${recorded} m per unit`}, from the points file` } : detectUnits(raw);
    scale = units.scale;
    let parts = 0;
    const plain = new THREE.MeshBasicMaterial();
    root.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { parts++; m.material = plain; } });
    // store the proxy as GLB in metres, so published tours load one compact file
    const wrap = new THREE.Group();
    wrap.add(root);
    wrap.scale.setScalar(scale);
    w.modelBlob = await exportGlb(wrap);
    wrap.remove(root);
    engine.setModel(root, scale, false);
    w.modelRoot = root; w.modelUnits = units.name;
    const sz = engine.bbox.getSize(new THREE.Vector3());
    w.modelName = model.name;
    setStep(w, 'model', { state: 'ok', detail: `${model.name} · ${units.name} · ${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m · ${parts} parts${base ? ' · replaces the saved model' : ''}` });
    emit(w, { kind: 'storage' });
  } catch (e) {
    setStep(w, 'model', { state: 'bad', detail: `Couldn’t read ${model.name}: ${e instanceof Error ? e.message : 'unknown error'}` });
    return finish(w, true);
  }

  // 2 · camera points (an update can use the saved positions instead)
  if (!cams && base) setStep(w, 'cams', { state: 'ok', detail: 'Using the saved camera positions. New cameras need a points file.' });
  else if (!cams) setStep(w, 'cams', { state: 'bad', detail: 'No points file. Add the .txt from the 3ds Max camera export.' });
  else {
    try {
      w.camFile = parseCameraFile(await cams.text());
      // points written in metres stay put if the model's scale changes; points in model units move with it
      w.scaleCams = w.camFile.units !== 'm';
      // cameras no render matches (renders match by name, or by a name that contains the camera's)
      const used = new Set(images.map((f) => matchCamera(baseName(f.name), w.camFile!)?.name.toLowerCase()).filter(Boolean));
      const without = w.camFile.cameras.filter((c) => !used.has(c.name.toLowerCase())).length;
      setStep(w, 'cams', {
        state: 'ok',
        detail: `${cams.name} · ${w.camFile.cameras.length} cameras${w.camFile.hasYaw ? ' with headings' : ''}${without ? ` · ${without} without renders, ignored` : ''}`,
      });
    } catch {
      setStep(w, 'cams', { state: 'bad', detail: `${cams.name} has no camera points. Each line should read: name x y z` });
    }
  }
  if (!w.camFile && !base) return finish(w, true);

  // depth maps: read each one's camera and a small copy now; the full file is read again later
  const depthIn: DepthIn[] = [];
  for (let i = 0; i < exrs.length; i++) {
    setStep(w, depthOnly ? 'model' : 'renders', { state: 'busy', detail: `Reading depth map ${i + 1} of ${exrs.length}`, progress: i / exrs.length });
    try {
      const im = await readExr(await exrs[i].arrayBuffer());
      const sm = shrink(im.data, im.width, im.height, 1024);
      depthIn.push({ file: exrs[i], name: exrs[i].name, cam: im.cameraTransform, small: sm.data, sw: sm.w, sh: sm.h });
    } catch { /* an unreadable file is reported with the depth maps below */ }
    await nextFrame();
  }
  // points not in metres and no model to measure them against: the units that put the cameras at eye height
  if (depthOnly && w.camFile && w.camFile.units !== 'm') {
    const heights = w.camFile.cameras.map((c) => (w.camFile!.zUp ? c.position[2] : c.position[1])).sort((a, b) => a - b);
    const mid = heights[Math.floor(heights.length / 2)] ?? 1.5;
    const unit = w.camFile.modelScale ?? [1, 0.01, 0.001, 0.0254, 0.3048].reduce((a, b) => (Math.abs(Math.log((mid * b) / 1.5)) < Math.abs(Math.log((mid * a) / 1.5)) ? b : a));
    engine.modelScale = unit;
  }
  // renders named differently from their cameras (Pano_0000 for ExportCam_001): the camera position in each
  // render's EXR finds its camera, in whichever scene units match most of them
  const camByRender = new Map<string, CameraPoint>();
  if (w.camFile && depthIn.some((d) => d.cam)) {
    const cams3 = w.camFile.cameras.map((c) => { const v = cameraToWorld(c, w.camFile!, engine.modelScale); return { c, x: v.x, y: v.y, z: v.z }; });
    let best = new Map<string, CameraPoint>();
    for (const unit of SCENE_UNITS) {
      const m = new Map<string, CameraPoint>();
      for (const d of depthIn) if (d.cam) { const hit = nearestTo(cameraPoint(d.cam, unit), cams3, 0.05); if (hit) m.set(depthName(d.name), hit.c); }
      if (m.size > best.size) best = m;
    }
    for (const [k, v] of best) camByRender.set(k, v);
    // the cameras step counted only name matches: count these too
    const named = new Set(images.map((f) => (matchCamera(baseName(f.name), w.camFile!) ?? camByRender.get(baseName(f.name).toLowerCase()))?.name.toLowerCase()).filter(Boolean));
    const without = w.camFile.cameras.filter((c) => !named.has(c.name.toLowerCase())).length;
    const st = w.steps.find((x) => x.id === 'cams')!;
    setStep(w, 'cams', { detail: st.detail.replace(/ · \d+ without renders, ignored$/, '') + (without ? ` · ${without} without renders, ignored` : '') + (best.size ? ` · ${best.size} found by the camera in their depth map` : '') });
  }
  const headingByRender = new Map(depthIn.filter((d) => d.cam).map((d) => [depthName(d.name), headingFromCamera(d.cam!)]));

  // 3 · renders: new ones, ones that overwrite a saved render by name, and saved ones kept as they are
  if (!images.length && !base) { setStep(w, 'renders', { state: 'bad', detail: 'No renders found. Add your equirectangular JPEGs.' }); return finish(w, true); }
  const saved = new Map((base?.panos ?? []).map((p) => [p.name.toLowerCase(), p]));
  const known = new Set<string>(); // panos whose heading is already known (saved, or from the points file)
  let replaced = 0;
  const baseW = Math.min(isTouch() ? 1024 : 2048, engine.maxTextureSize);
  let notWide = 0, unmatched = 0;
  for (let i = 0; i < images.length; i++) {
    const f = images[i];
    setStep(w, 'renders', { state: 'busy', detail: `Reading ${i + 1} of ${images.length}`, progress: i / images.length });
    const old = saved.get(baseName(f.name).toLowerCase());
    if (old) { saved.delete(old.name.toLowerCase()); replaced++; }
    const p: WorkPano = {
      id: old?.id ?? crypto.randomUUID(), name: baseName(f.name), file: f, thumbUrl: '', thumb: null, base: null, mobile: null, full: null,
      status: 'busy', note: '', level: null, ep: null, replaces: !!old,
    };
    try {
      const bmp = await createImageBitmap(f);
      const W = bmp.width, H = bmp.height;
      if (Math.abs(W / H - 2) > 0.05) notWide++;
      p.thumb = await canvasBlob(bmp, W * 0.32, H * 0.4, 480, 300, W * 0.34, H * 0.3, 0.8);
      p.thumbUrl = URL.createObjectURL(p.thumb);
      w.panos.push(p);
      emit(w, { kind: 'pano', pano: p, added: true });
      p.base = await canvasBlob(bmp, W, H, Math.min(2048, W), Math.min(1024, H));
      p.mobile = await canvasBlob(bmp, W, H, Math.min(1024, W), Math.min(512, H), 0, 0, 0.82);
      p.full = /jpe?g$/i.test(f.type || f.name) && W <= 8192 ? f : await canvasBlob(bmp, W, H, Math.min(8192, W), Math.min(4096, H), 0, 0, 0.9);
      const cam = w.camFile ? matchCamera(p.name, w.camFile) ?? camByRender.get(p.name.toLowerCase()) ?? null : null;
      if (!cam && !old) { p.status = 'bad'; p.note = 'no camera'; unmatched++; }
      else {
        const tex = panoTextureFrom(bmp, Math.min(W, baseW), engine.renderer);
        // a re-render of a saved view keeps its position and heading unless a new points file says otherwise
        const position = cam ? cameraToWorld(cam, w.camFile!, engine.modelScale) : new THREE.Vector3(old!.x, old!.y, old!.z);
        const yawDeg = cam?.yaw ?? old?.yaw ?? headingByRender.get(p.name.toLowerCase()) ?? null;
        const ep = engine.addPano({ id: p.id, name: p.name, position, yaw: (yawDeg ?? 0) * (Math.PI / 180), texture: tex, data: { edge: imageEdges(bmp) } });
        if (yawDeg != null) known.add(p.id);
        p.ep = ep;
        p.cam = cam?.name;
        p.status = 'ok';
      }
      bmp.close?.();
    } catch {
      p.status = 'bad'; p.note = 'unreadable';
      if (!w.panos.includes(p)) w.panos.push(p);
    }
    emit(w, { kind: 'pano', pano: p, added: false });
    emit(w, { kind: 'storage' });
    await nextFrame();
  }
  // saved renders that weren't in the drop stay as they are
  let kept = 0;
  for (const old of saved.values()) {
    setStep(w, 'renders', { state: 'busy', detail: `Loading saved render ${++kept} of ${saved.size}`, progress: kept / saved.size });
    const p: WorkPano = {
      id: old.id, name: old.name, file: null, kept: true, thumbUrl: old.thumbUrl, thumb: null, base: null, mobile: null, full: null,
      status: 'busy', note: 'kept', level: null, ep: null,
    };
    w.panos.push(p);
    emit(w, { kind: 'pano', pano: p, added: true });
    try {
      const { texture } = await loadPanoTexture(old.imageUrl, Math.min(1024, baseW), engine.renderer);
      const cam = w.camFile ? matchCamera(old.name, w.camFile) : null;
      const position = cam ? cameraToWorld(cam, w.camFile!, engine.modelScale) : new THREE.Vector3(old.x, old.y, old.z);
      p.ep = engine.addPano({ id: p.id, name: p.name, position, yaw: (cam?.yaw ?? old.yaw) * (Math.PI / 180), texture, data: {} });
      p.cam = cam?.name;
      known.add(p.id);
      p.status = 'ok';
    } catch { p.status = 'bad'; p.note = 'couldn’t load'; }
    emit(w, { kind: 'pano', pano: p, added: false });
    await nextFrame();
  }
  // several renders on one camera are finish schemes: one stays the main render, the rest become alternates
  const altCount = groupSchemes(w);
  const placed = w.panos.filter((p) => p.ep).length;
  const newOnes = images.length - replaced - altCount;
  setStep(w, 'renders', {
    state: placed === 0 ? 'bad' : unmatched || notWide || dupes ? 'warn' : 'ok', progress: undefined,
    detail: base
      ? [newOnes ? `${newOnes} new` : '', replaced ? `${replaced} replaced` : '', kept ? `${kept} kept` : '', unmatched ? `${unmatched} with no camera` : '', notWide ? `${notWide} not 2:1` : '', dupes ? `${dupes} duplicate name${dupes === 1 ? '' : 's'} skipped (newest kept)` : ''].filter(Boolean).join(' · ') || 'No renders'
      : `${placed} of ${images.length - altCount} matched to cameras${altCount ? ` · ${altCount} alternate finish${altCount === 1 ? '' : 'es'} (${w.mainScheme ?? 'first'} is the main one)` : ''}${unmatched ? ` · ${unmatched} with no camera` : ''}${notWide ? ` · ${notWide} not 2:1` : ''}${dupes ? ` · ${dupes} duplicate name${dupes === 1 ? '' : 's'} skipped (newest kept)` : ''}`,
  });
  if (!placed) return finish(w, true);

  // depth maps: each to its render, the range worked out, written as PNG; with no model, the proxy made from them
  if (depthIn.length) {
    const ok = await attachDepth(w, depthIn, depthOnly);
    if (!ok && depthOnly) return finish(w, true);
  }

  // a new model: check its units against the cameras, which should stand about eye height above a floor
  if (model && !base && w.camFile && !w.camFile.modelScale) {
    const fit = fitScale(w);
    if (fit && Math.abs(fit.scale - engine.modelScale) > 1e-9) {
      applyScale(w, fit.scale, false);
      w.modelUnits = fit.name.toLowerCase();
      w.rescaled = false;
      w.modelBlob = await bakeModel(w);
      const sz = engine.bbox.getSize(new THREE.Vector3());
      setStep(w, 'model', { state: 'ok', detail: `${w.modelName} · ${w.modelUnits} (to fit the cameras) · ${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m` });
    }
  }

  // 4 · levels and depth
  setStep(w, 'levels', { state: 'busy', detail: 'Measuring the model from each render' });
  await nextFrame();
  engine.refresh();
  w.levels = base && !model && base.levels.length ? base.levels : engine.levels;
  const between = assignLevels(w);
  setStep(w, 'levels', {
    state: 'ok',
    detail: `${w.levels.length} level${w.levels.length === 1 ? '' : 's'}: ${w.levels.map((l, i) => `${levelName(i)} at ${l.toFixed(1)} m`).join(' · ')}${between ? ` · ${between} stair view${between === 1 ? '' : 's'} between` : ''}`,
  });

  // 5 · headings
  const eps = w.panos.filter((p) => p.ep && !known.has(p.id)).map((p) => p.ep!);
  if (w.depthBuilt) setStep(w, 'headings', eps.length
    ? { state: 'warn', detail: `${eps.length} render${eps.length === 1 ? '' : 's'} with no heading in the points file or EXR; check them` }
    : { state: 'ok', detail: 'Taken from your points file and depth maps' });
  else if (!eps.length) setStep(w, 'headings', { state: 'ok', detail: w.camFile?.hasYaw ? 'Taken from your points file' : 'Kept from the saved renders' });
  else if (w.camFile?.hasYaw) setStep(w, 'headings', { state: 'ok', detail: 'Taken from your points file' });
  else {
    setStep(w, 'headings', { state: 'busy', detail: 'Matching each render to the model', progress: 0 });
    const r = await detectHeadings(engine, eps, {
      onProgress: (d, t) => setStep(w, 'headings', { state: 'busy', detail: `Matching ${d} of ${t} to the model`, progress: d / t }),
    });
    let flagged = 0;
    for (const p of w.panos) {
      if (!p.ep) continue;
      p.headingOwn = p.ep.data.headingOwn as number | undefined;
      p.headingShared = p.ep.data.headingShared as number | undefined;
      if (p.ep.data.headingOk === false) { p.status = 'warn'; p.note = 'check heading'; flagged++; emit(w, { kind: 'pano', pano: p, added: false }); }
    }
    if (r?.flip) engine.settings.flip = true;
    setStep(w, 'headings', {
      state: flagged ? 'warn' : 'ok', progress: undefined,
      detail: r?.shared
        ? `All cameras share a heading of ${((p0) => (p0 ?? 0).toFixed(1))(w.panos.find((p) => p.headingShared != null)?.headingShared)}°${flagged ? ` · ${flagged} to check` : ''}${r.flip ? ' · renders are mirrored' : ''}`
        : `${r?.confident ?? 0} of ${eps.length} matched clearly${flagged ? ` · ${flagged} to check` : ''}`,
    });
  }
  finish(w, false);
}

/**
 * Matches the drop's depth maps to the placed renders, works out their range (against the model, or with no
 * model by how the renders agree with each other), writes each as the PNG the tour reads and, with no model,
 * makes the proxy from them. Returns false when nothing could be used.
 */
async function attachDepth(w: Work, depthIn: DepthIn[], depthOnly: boolean): Promise<boolean> {
  const engine = w.engine;
  const step: StepId = depthOnly ? 'model' : 'renders';
  const before = { ...w.steps.find((x) => x.id === 'renders')! };
  const placed = w.panos.filter((p) => p.ep && !p.kept);
  const targets = placed.map((p) => ({ name: p.name, x: p.ep!.pos.x, y: p.ep!.pos.y, z: p.ep!.pos.z, p }));
  const match = matchDepth(depthIn.map((d) => ({ name: d.name, cam: d.cam })), targets);
  const pairs = depthIn.map((d, i) => ({ d, p: match[i]?.p ?? null })).filter((x): x is { d: DepthIn; p: WorkPano } => !!x.p);
  const flip = !!engine.settings.flip;
  setStep(w, step, { state: 'busy', detail: 'Working out the depth range' });
  await nextFrame();
  let fit: DepthFit | null = null;
  if (!depthOnly && engine.meshes.length) {
    for (const { d, p } of pairs.slice(0, 8)) {
      const f = engine.fitPanoDepth(p.ep!, d.small, d.sw, d.sh, d.sw, true)?.fit ?? null;
      if (f && (!fit || f.agree > fit.agree)) fit = f;
    }
    if (fit && fit.agree < 0.35) fit = null;
  } else {
    fit = fitDepthByViews(pairs.map(({ d, p }) => ({ raw: d.small, w: d.sw, h: d.sh, pos: p.ep!.pos, yaw: p.ep!.yaw })), flip, true);
    if (fit && fit.agree < 0.25) fit = null;
  }
  const notMatched = depthIn.length - pairs.length;
  if (!fit) {
    setStep(w, step, depthOnly
      ? { state: 'bad', detail: 'Couldn’t make a model from the depth maps: they don’t agree with each other or the camera points. Add a model file.', progress: undefined }
      : { state: 'warn', detail: `${before.detail} · the depth maps don’t line up with the model, so they were left out`, progress: undefined });
    return false;
  }
  const builder = depthOnly ? new DepthProxyBuilder(flip) : null;
  for (let i = 0; i < pairs.length; i++) {
    const { d, p } = pairs[i];
    setStep(w, step, { state: 'busy', detail: `${depthOnly ? 'Making the model from' : 'Preparing'} depth map ${i + 1} of ${pairs.length}`, progress: i / pairs.length });
    try {
      const im = await readExr(await d.file.arrayBuffer());
      const r = applyDepthFit(im.data, im.width, im.height, fit, 2048);
      builder?.add({ data: r.data, w: r.w, h: r.h, pos: p.ep!.pos, yaw: p.ep!.yaw });
      p.depthPng = await depthPng(r.data, r.w, r.h);
    } catch { /* that render goes without */ }
    d.small = new Float32Array(0);
    await nextFrame();
  }
  const withDepth = w.panos.filter((p) => p.depthPng).length;
  emit(w, { kind: 'storage' });
  if (builder) {
    setStep(w, 'model', { state: 'busy', detail: `Fusing ${withDepth} depth maps into one model`, progress: 0 });
    await nextFrame();
    const group = await builder.fuse((f) => setStep(w, 'model', { state: 'busy', detail: `Fusing ${withDepth} depth maps into one model`, progress: f }));
    // written before the engine gives it its own material (the exporter can't write that)
    w.modelBlob = await exportGlb(group);
    engine.setModel(group, 1, false);
    w.modelRoot = group; w.modelUnits = 'metres'; w.fileUnit = 1; w.fileUnitKnown = true; w.scaleCams = false;
    w.modelName = 'Made from depth maps';
    w.depthBuilt = true;
    const sz = engine.bbox.getSize(new THREE.Vector3());
    setStep(w, 'model', { state: 'warn', progress: undefined,
      detail: `Made from ${withDepth} depth maps · ${sz.x.toFixed(1)} × ${sz.z.toFixed(1)} m · only what the renders saw, so the 3D plan, clicks and measuring are less exact than with a model file${notMatched ? ` · ${notMatched} depth map${notMatched === 1 ? '' : 's'} matched no render` : ''}` });
    emit(w, { kind: 'storage' });
  } else {
    setStep(w, 'renders', { state: before.state, detail: `${before.detail} · ${withDepth} with depth maps${notMatched ? ` · ${notMatched} depth map${notMatched === 1 ? '' : 's'} matched no render` : ''}`, progress: undefined });
  }
  return withDepth > 0;
}

/** The part of each name that differs within a group, e.g. "Dark 2", cut at word breaks. */
export function schemeNames(names: string[]): string[] {
  const sep = /[\s_\-.]/;
  let pre = 0;
  while (names.every((n) => n.length > pre && n[pre] === names[0][pre])) pre++;
  while (pre > 0 && !sep.test(names[0][pre - 1])) pre--;
  let suf = 0;
  const minLen = Math.min(...names.map((n) => n.length));
  while (suf < minLen - pre && names.every((n) => n[n.length - 1 - suf] === names[0][names[0].length - 1 - suf])) suf++;
  while (suf > 0 && !sep.test(names[0][names[0].length - suf])) suf--;
  return names.map((n, i) => n.slice(pre, n.length - suf).replace(/^[\s_\-.]+|[\s_\-.]+$/g, '').replace(/_/g, ' ').trim() || `Scheme ${i + 1}`);
}

/**
 * Renders that matched the same camera are finishes of one view (Light, Dark...). Saved renders stay the main
 * one; otherwise the scheme named in the most renders does. The others become alternates and leave the engine,
 * so they don't blend into each other. Returns how many became alternates.
 */
function groupSchemes(w: Work): number {
  const byCam = new Map<string, WorkPano[]>();
  for (const p of w.panos) if (p.ep && p.cam) { const k = p.cam.toLowerCase(); byCam.set(k, [...(byCam.get(k) ?? []), p]); }
  // renders numbered differently (Pano_01, Pano_02) aren't finishes of one view: their names matched the wrong camera
  const firstNum = (s: string) => +(s.match(/\d+/)?.[0] ?? NaN);
  const clash = (g: WorkPano[]) => new Set(g.map((p) => firstNum(p.name)).filter((x) => !Number.isNaN(x))).size > 1;
  for (const g of byCam.values()) {
    if (g.length < 2 || !clash(g)) continue;
    for (const p of g) if (!p.kept) { p.status = 'warn'; p.note = 'same camera'; }
  }
  const groups = [...byCam.values()].filter((g) => g.filter((p) => !p.kept).length && g.length > 1 && !clash(g));
  if (!groups.length) return 0;
  const label = new Map<WorkPano, string>();
  for (const g of groups) { const names = schemeNames(g.map((p) => p.name)); g.forEach((p, i) => label.set(p, names[i])); }
  const all = w.panos.filter((p) => p.ep).map((p) => p.name.toLowerCase());
  const esc = (s: string) => s.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const score = (s: string) => { const re = new RegExp(`(^|[^a-z0-9])${esc(s)}([^a-z0-9]|$)`); return all.filter((n) => re.test(n.replace(/_/g, ' '))).length; };
  const mains = new Map<string, number>();
  let n = 0;
  for (const g of groups) {
    const main = g.find((p) => p.kept) ?? [...g].sort((a, b) => (score(label.get(b)!) - score(label.get(a)!)) || natural(a.name, b.name))[0];
    if (!main.kept) mains.set(label.get(main)!, (mains.get(label.get(main)!) ?? 0) + 1);
    for (const p of g) {
      if (p === main) continue;
      p.altOf = main.id; p.scheme = label.get(p)!;
      const i = w.engine.panos.indexOf(p.ep!);
      if (i >= 0) { w.engine.panos.splice(i, 1); p.ep!.baseTex.dispose(); }
      p.ep = null; p.status = 'ok'; p.note = 'alternate';
      n++;
    }
  }
  w.mainScheme = [...mains].sort((a, b) => b[1] - a[1])[0]?.[0];
  return n;
}

/** Puts each render on its level; returns how many sit between levels (stairs). */
function assignLevels(w: Work): number {
  const placed = w.panos.filter((p) => p.ep);
  const at = assignToLevels(w.levels, placed.map((p) => p.ep!.pos.y));
  placed.forEach((p, i) => { p.level = at[i]; });
  return at.filter((i) => i == null).length;
}

/** Model units offered on the check screen: scale from the model file's units to metres. */
export const MODEL_UNITS: Array<[number, string]> = [
  [1, 'Metres'], [0.01, 'Centimetres'], [0.001, 'Millimetres'], [1000, 'Kilometres'], [0.0254, 'Inches'], [0.3048, 'Feet'],
];

/**
 * How well a model scale suits the cameras: each render should stand above a floor at about eye height
 * (1.5 m). Returns the median eye height and a score (lower is better).
 */
export function scaleFit(w: Work, s: number): { score: number; eye: number | null } {
  const e = w.engine;
  const k = s / e.modelScale;
  const rc = new THREE.Raycaster();
  const down = new THREE.Vector3(0, -1, 0);
  const scores: number[] = [], eyes: number[] = [];
  const eps = w.panos.filter((p) => p.ep).map((p) => p.ep!);
  const step = Math.max(1, Math.floor(eps.length / 40));
  for (let i = 0; i < eps.length; i += step) {
    // where this camera would stand at scale s, in today's model space
    const q = w.scaleCams ? eps[i].pos.clone() : eps[i].pos.clone().divideScalar(k);
    rc.set(q, down);
    const hit = rc.intersectObjects(e.meshes, false)[0];
    if (!hit) { scores.push(3); continue; }
    const d = hit.distance * k;
    eyes.push(d);
    scores.push(Math.min(3, Math.abs(Math.log(Math.max(d, 1e-6) / 1.5))));
  }
  const med = (a: number[]) => { if (!a.length) return null; const b = [...a].sort((x, y) => x - y); return b[Math.floor(b.length / 2)]; };
  return { score: med(scores) ?? 3, eye: med(eyes) };
}

/** The model units that best fit the cameras, or null when nothing fits clearly. `scale` is the engine's
 * scale for `modelRoot`; `name` the units (or a factor, for an older saved model whose units aren't known). */
export function fitScale(w: Work): { scale: number; name: string } | null {
  if (!w.modelRoot || !w.engine.meshes.length) return null;
  const now = scaleFit(w, w.engine.modelScale).score;
  // an older saved model could have been read in any units, so try every factor between two units
  const options: Array<[number, string]> = w.fileUnitKnown
    ? MODEL_UNITS.map(([u, n]) => [u / w.fileUnit, n])
    : [...new Map(MODEL_UNITS.flatMap(([a]) => MODEL_UNITS.map(([b]) => a / b)).map((r) => [+r.toPrecision(6), r])).values()].map((r) => [r, `×${+r.toPrecision(4)}`]);
  let best: { scale: number; name: string; score: number } | null = null;
  for (const [s, name] of options) {
    const { score } = scaleFit(w, s);
    if (!best || score < best.score - 1e-9) best = { scale: s, name, score };
  }
  // only move away from the current scale when the fit is clearly better
  if (!best || best.score > 1 || best.score > now - 0.2) return null;
  return best;
}

/**
 * Sets the model's scale (file units to metres). Cameras move with the model when their points were in
 * model units (`scaleCams`). Recomputes floors, depth and levels unless `refresh` is false.
 */
export function applyScale(w: Work, s: number, refresh = true): void {
  const e = w.engine;
  if (!w.modelRoot || !(s > 0) || Math.abs(s - e.modelScale) < 1e-12) return;
  const k = s / e.modelScale;
  e.setModel(w.modelRoot, s, false);
  for (const p of w.panos) if (p.ep) { if (w.scaleCams) p.ep.pos.multiplyScalar(k); p.ep.dirty = true; }
  w.rescaled = true;
  if (!refresh) return;
  e.refresh();
  w.levels = e.levels.slice();
  assignLevels(w);
}

/** Puts the model and cameras back how they were read (positions saved with `snapshotScale`). */
export function snapshotScale(w: Work): { scale: number; pos: Map<EnginePano, THREE.Vector3> } {
  return { scale: w.engine.modelScale, pos: new Map(w.panos.filter((p) => p.ep).map((p) => [p.ep!, p.ep!.pos.clone()])) };
}
export function restoreScale(w: Work, snap: { scale: number; pos: Map<EnginePano, THREE.Vector3> }): void {
  const e = w.engine;
  if (w.modelRoot && Math.abs(snap.scale - e.modelScale) > 1e-12) e.setModel(w.modelRoot, snap.scale, false);
  snap.pos.forEach((v, ep) => { ep.pos.copy(v); ep.dirty = true; });
  e.refresh();
  w.levels = e.levels.slice();
  assignLevels(w);
  w.rescaled = false;
}

/** Writes the model again at its current scale, as GLB in metres. */
export async function bakeModel(w: Work): Promise<Blob | null> {
  const root = w.modelRoot;
  if (!root) return w.modelBlob;
  const parent = root.parent;
  const mats = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
  const plain = new THREE.MeshBasicMaterial();
  root.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { mats.set(m, m.material); m.material = plain; } });
  const wrap = new THREE.Group();
  wrap.add(root);
  wrap.scale.setScalar(w.engine.modelScale);
  try { return await exportGlb(wrap); } finally {
    wrap.remove(root);
    parent?.add(root);
    mats.forEach((mat, m) => { m.material = mat; });
    parent?.updateMatrixWorld(true);
  }
}

function finish(w: Work, failed: boolean): void {
  w.running = false;
  w.done = !failed;
  w.failed = failed;
  if (!failed) {
    w.engine.resetMode('doll');
  }
  emit(w, { kind: 'done' });
}
